import { Injectable } from '@angular/core';
import { ProductControllerService, ProductControllerXService, SubProductDTO } from '@iota/iota-management-portal-v4-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, filter, forkJoin, map, mergeMap, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ProductListService } from 'src/app/shared/services/product-list.service';
import { getLocationHeader, replaceRestrictionListSeparator } from 'src/app/shared/utils';
import {
    AddSubSubProduct,
    AddUpdateRemoveProductAttributes,
    AddUpdateRemoveSubProductAttributes,
    DeletePricingOption,
    LoadProductDetails,
    LoadProductDetailsFailure,
    LoadProductDetailsSuccess,
    LoadProductsByIds,
    LoadProductsByIdsSuccess,
    ProductActionTypes,
    ReloadProduct,
    ReloadProductSuccess,
    RemoveProductAttachment,
    RemoveProductNote,
    RemoveSubProduct,
    RemoveSubProductRestriction,
    UpdatePricingOptionsOrder,
    UpdatePricingOptionsOrderSuccess,
    UpsertPricingOption,
    UpsertProduct,
    UpsertProductAttachment,
    UpsertProductNote,
    UpsertProductSuccess,
    UpsertSubProduct,
    UpsertSubProductRestriction
} from '../actions/product.actions';
import { selectedProduct } from '../store';
import { selectedLanguage } from '../reducer/language.reducer';

@Injectable()
export class ProductEffects {
    // The by-id endpoint does not know sub product ids; the full list carries them inside their parent.
    loadProductDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadProductDetails),
            withLatestFrom(this.store.select(selectedLanguage)),
            switchMap(([{ productId }, language]) =>
                this.apiService.getAllProductsByIds([productId]).pipe(
                    switchMap((products) => (products?.length > 0 ? of(products) : this.apiService.getAllProducts())),
                    take(1),
                    map((products) => LoadProductDetailsSuccess({ products, language: language?.countryCode || 'de' })),
                    catchError((error) => of(LoadProductDetailsFailure({ error })))
                )
            )
        )
    );

    loadProductsByIds$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadProductsByIds),
            mergeMap(({ productIds }) =>
                this.apiService.getAllProductsByIds(productIds).pipe(
                    take(1),
                    map((products) => LoadProductsByIdsSuccess({ products })),
                    // No request state to report to: a consumer sees the failure as the products staying absent.
                    catchError(() => EMPTY)
                )
            )
        )
    );

    reloadProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReloadProduct, UpsertProductSuccess, UpdatePricingOptionsOrderSuccess),
            mergeMap(({ productId }) =>
                this.apiService.getProduct(productId).pipe(
                    take(1),
                    map((product) => ({ type: ProductActionTypes.ReloadProductSuccess, product })),
                    catchError((error) => of({ type: ProductActionTypes.ReloadProductFailure, error: error }))
                )
            )
        )
    );

    // Every product mutation ends in this action; the filter keeps an untouched cache from being fetched for the first time.
    refreshCachedProductList$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ReloadProductSuccess),
                filter(() => this.productListService.requested()),
                tap(() => this.productListService.reload())
            ),
        { dispatch: false }
    );

    upsertProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertProduct),
            mergeMap(({ product }) =>
                this.apiService.upsertProduct(product, 'response').pipe(
                    take(1),
                    map((response) => ({
                        type: ProductActionTypes.UpsertProductSuccess,
                        productId: getLocationHeader(response)
                    })),
                    catchError((error) => of({ type: ProductActionTypes.UpsertProductFailure, error: error }))
                )
            )
        )
    );

    addUpdateRemoveProductAttributes = createEffect(() =>
        this.actions$.pipe(
            ofType(AddUpdateRemoveProductAttributes),
            withLatestFrom(this.store.select(selectedProduct)),
            mergeMap(([{ attributeResult }, product]) =>
                forkJoin([
                    attributeResult.upsertedAttributes?.length > 0
                        ? this.apiService.upsertProductAttributes(product.productId, attributeResult.upsertedAttributes)
                        : of(null),
                    attributeResult.removedAttributeIds?.length > 0
                        ? this.apiService.removeProductAttributes(attributeResult.removedAttributeIds)
                        : of(null)
                ]).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId: product.productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    addUpdateRemoveSubProductAttributes = createEffect(() =>
        this.actions$.pipe(
            ofType(AddUpdateRemoveSubProductAttributes),
            mergeMap(({ attributeResult, subProduct }) =>
                forkJoin([
                    attributeResult.upsertedAttributes?.length > 0
                        ? this.apiService.upsertSubProductAttributes(subProduct.subProductId, attributeResult.upsertedAttributes)
                        : of(null),
                    attributeResult.removedAttributeIds?.length > 0
                        ? this.apiService.removeSubProductAttributes(attributeResult.removedAttributeIds)
                        : of(null)
                ]).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId: subProduct.productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    upsertProductNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertProductNote),
            withLatestFrom(this.store.select(selectedProduct)),
            mergeMap(([{ note }, product]) => {
                let productId = product.productId;

                if ((product as SubProductDTO).subProductId) {
                    productId = (product as SubProductDTO).subProductId;
                }

                return this.apiService.upsertProductNote(productId, note).pipe(
                    take(1),
                    map(() => ({
                        type: ProductActionTypes.ReloadProduct,
                        productId: product.productId
                    })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                );
            })
        )
    );

    removeProductNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveProductNote),
            withLatestFrom(this.store.select(selectedProduct)),
            mergeMap(([{ noteId }, { productId }]) =>
                this.apiService.removeProductNote(noteId).pipe(
                    take(1),
                    map(() => ({
                        type: ProductActionTypes.ReloadProduct,
                        productId
                    })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    upsertPricingOption$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPricingOption),
            mergeMap(({ pricingOption }) =>
                this.apiService.upsertProductPricingOption(pricingOption).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId: pricingOption.productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    deleteProductPricingOption$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeletePricingOption),
            mergeMap(({ pricingOptionId, productId }) =>
                this.apiService.deleteProductPricingOption(pricingOptionId).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    updatePricingOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdatePricingOptionsOrder),
            mergeMap(({ pricingOptions, productId }) =>
                this.apiService.upsertMultipleProductPricingOptions(pricingOptions).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.UpdatePricingOptionsOrderSuccess, productId, pricingOptions })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    upsertSubProductRestriction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertSubProductRestriction),
            mergeMap(({ restriction, productId }) => {
                replaceRestrictionListSeparator(restriction);

                return this.apiService.upsertSubProductRestriction(restriction).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                );
            })
        )
    );

    upsertSubProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertSubProduct),
            mergeMap(({ subProduct, productId }) => {
                return this.apiService.upsertSubProduct(subProduct).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                );
            })
        )
    );

    // TODO
    addSubSubProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddSubSubProduct),
            mergeMap(({ subProduct, productId }) => {
                return this.apiService.upsertSubSubProduct(subProduct).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                );
            })
        )
    );

    removeSubProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveSubProduct),
            mergeMap(({ subProductId, productId }) => {
                return this.apiService.removeSubProduct(subProductId).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                );
            })
        )
    );

    removeSubProductRestriction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveSubProductRestriction),
            mergeMap(({ restrictionId, productId }) =>
                this.apiService.removeSubProductRestriction(restrictionId).pipe(
                    take(1),
                    map(() => ({ type: ProductActionTypes.ReloadProduct, productId })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    // TODO! 500 Error
    upsertProductAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertProductAttachment),
            mergeMap(({ productId, file }) =>
                this.apiXService.upsertProductAttachment(file, { infinkonProductId: productId }).pipe(
                    take(1),
                    map(() => ({
                        type: ProductActionTypes.ReloadProduct,
                        productId
                    })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    removeProductAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveProductAttachment),
            mergeMap(({ productId, documentDescriptionId }) =>
                this.apiService.removeProductAttachment(documentDescriptionId).pipe(
                    take(1),
                    map(() => ({
                        type: ProductActionTypes.ReloadProduct,
                        productId
                    })),
                    catchError((error) => of({ type: ProductActionTypes.ProductActionFailure, error: error }))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private apiService: ProductControllerService,
        private apiXService: ProductControllerXService,
        private productListService: ProductListService
    ) {}
}
